import React, { useRef, useState, useEffect } from 'react';
import { Upload, Button, message, Dropdown } from 'antd';
import { UploadOutlined, CameraOutlined, FolderOpenOutlined, DeleteOutlined } from '@ant-design/icons';
import { deleteDocuments } from '../../services/admin-services';
import { useParams } from 'react-router-dom';
import { fetchLeadDetailsServices } from '../../app/auth/slice/authSlice';
import { useDispatch, useSelector } from 'react-redux';

const UploadButton = ({ label, name, fileList, setFileList, fileType, fileSizeLimit, documentType, className, value, onChange, hideDeleteIcon = false }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const leadViewDetails = useSelector((state) => state?.auth?.leadDetails);
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  // Initialize isMobile immediately
  const checkIfMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isSmallScreen = window.innerWidth <= 768;
    
    return mobileRegex.test(userAgent) || (isTouchDevice && isSmallScreen);
  };

  const [isMobile, setIsMobile] = useState(checkIfMobile());

  // Update on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(checkIfMobile());
    };

    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFileChange = (info, fieldName) => {
    setFileList({
      ...fileList,
      [fieldName]: info.fileList,
    });
    
    // Sync with form validation
    if (onChange) {
      onChange(info.fileList.length > 0 ? info.fileList : undefined);
    }
  };

  const validateFile = (file) => {
    const isAllowedType = fileType.includes(file.type);
    if (!isAllowedType) {
      message.error(`You can only upload ${fileType?.join(', ')} file!`);
      return Upload.LIST_IGNORE;
    }
    const isLtSizeLimit = file.size / 1024 / 1024 < fileSizeLimit;
    if (!isLtSizeLimit) {
      message.error(`File must be smaller than ${fileSizeLimit}MB!`);
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleRemove = async (file) => {
    if (!file.lastModifiedDate) {
      try {
        const documentId = file?.uid;
        const reqData = {
          document_id: documentId ? documentId : null,
          document_type: documentType,
          lead_id: id,
        };

        if (leadViewDetails?.documents) {
          const response = await deleteDocuments(reqData);
          message.success(response?.data?.message || 'File removed successfully');
          setFileList({
            ...fileList,
            [name]: fileList[name]?.filter((item) => item.uid !== file.uid),
          });
          dispatch(fetchLeadDetailsServices({ id: id }));
        }
      } catch (error) {
        message.error(`Failed to remove file: ${error.message}`);
      }
    } else {
      // For newly selected files (not yet uploaded to server)
      setFileList({
        ...fileList,
        [name]: fileList[name]?.filter((item) => item.uid !== file.uid),
      });
      if (onChange) {
        onChange(undefined);
      }
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const isValid = validateFile(file);
      if (isValid === true) {
        const newFile = {
          uid: `file-${Date.now()}`,
          name: file.name,
          status: 'done',
          originFileObj: file,
        };
        
        const updatedFileList = [...(fileList[name] || []), newFile].slice(-1);
        
        setFileList({
          ...fileList,
          [name]: updatedFileList,
        });

        if (onChange) {
          onChange(updatedFileList);
        }

      }
    }
    event.target.value = '';
  };

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileClick = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    } else if (file.originFileObj) {
      const url = URL.createObjectURL(file.originFileObj);
      window.open(url, '_blank');
    }
  };

  const menuItems = [
    {
      key: 'camera',
      icon: <CameraOutlined />,
      label: 'Take Photo',
      onClick: openCamera,
    },
    {
      key: 'gallery',
      icon: <FolderOpenOutlined />,
      label: 'Choose from Gallery',
      onClick: openFileSelector,
    },
  ];

  const currentFileList = value || fileList[name] || [];

  return (
    <div
      className={`exchange-upload ${label ? 'd-flex align-items-start justify-content-between' : ''} ${
        className ? className : ''
      }`}
    >
      {label ? <p className='label'>{label}</p> : null}
      <div style={{ width: '100%' }}>
        {isMobile && hideDeleteIcon ? (
          <>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
              placement="bottomLeft"
            >
              <Button icon={<UploadOutlined />}></Button>
            </Dropdown>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <input
              ref={fileInputRef}
              type="file"
              accept={fileType?.join(',')}
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            {currentFileList.length > 0 && (
              <div style={{
                marginTop: '10px',
                padding: '8px 12px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                width: '100%'
              }}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleFileClick(currentFileList[0]);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '14px',
                    color: '#1890ff',
                    textDecoration: 'none'
                  }}
                >
                  {currentFileList[0]?.name}
                </a>
                {!hideDeleteIcon && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(currentFileList[0])}
                    size="small"
                    style={{ marginLeft: '8px', flexShrink: 0 }}
                  />
                )}
              </div>
            )}
          </>
        ) : (
          <Upload
            customRequest={({ onSuccess }) => {
              setTimeout(() => {
                onSuccess('ok');
              }, 0);
            }}
            onChange={(info) => handleFileChange(info, name)}
            beforeUpload={validateFile}
            maxCount={1}
            accept={fileType?.join(',')}
            fileList={value || fileList[name]}
            showUploadList={{ showRemoveIcon: !hideDeleteIcon }}
            onRemove={leadViewDetails?.documents ? handleRemove : ''}
          >
            <Button icon={<UploadOutlined />}></Button>
          </Upload>
        )}
      </div>
    </div>
  );
};

export default UploadButton;